<template>
  <div :class="['cc-summary-outer', { collapsed }]">
    <div class="cc-summary-pane">
      <div class="cc-sp-collapse-btn" :title="collapsed ? 'Expand panel' : 'Collapse panel'" @click="collapsed = !collapsed">
        {{ collapsed ? '▶' : '◀' }}
      </div>

      <!-- Collapsed strip -->
      <div class="cc-sp-collapsed-strip">
        <div v-for="(label, i) in sectionLabels" :key="'icon-' + i" :class="['cc-sp-icon', stepStatus(i + 1)]" :title="`${label}: ${stepStatus(i + 1)}`">
          {{ i + 1 }}
        </div>
      </div>

      <!-- Main content -->
      <div class="cc-sp-main-content">
        <div class="cc-sp-hdr">
          <div class="cc-sp-title">Campaign Summary</div>
          <div class="cc-sp-badge">In progress</div>
        </div>

        <div class="cc-sp-section">
          <div class="cc-sp-lbl"><span :class="['cc-sn', stepStatus(1)]">1</span> Campaign Type</div>
          <div class="cc-sp-row"><span class="cc-sp-k">Type</span><span class="cc-sp-v">{{ summary.type || 'Not selected yet' }}</span></div>
        </div>

        <div class="cc-sp-section">
          <div class="cc-sp-lbl"><span :class="['cc-sn', stepStatus(2)]">2</span> {{ audienceSectionLabel }}</div>
          <div v-if="summary.country" class="cc-sp-row">
            <span class="cc-sp-k">Country</span><span class="cc-sp-v">{{ summary.country }}</span>
          </div>
          <div v-if="summary.customer" class="cc-sp-row">
            <span class="cc-sp-k">Customer</span><span class="cc-sp-v">{{ summary.customer }}</span>
          </div>
          <div v-if="summary.journey" class="cc-sp-row">
            <span class="cc-sp-k">Journey</span><span class="cc-sp-v">{{ summary.journey }}</span>
          </div>
          <div v-if="summary.channels && summary.channels.length" class="cc-sp-row">
            <span class="cc-sp-k">Channel</span><span class="cc-sp-v">{{ summary.channels.join(', ') }}</span>
          </div>
          <div v-if="summary.languages && summary.languages.length" class="cc-sp-row">
            <span class="cc-sp-k">Language</span><span class="cc-sp-v">{{ summary.languages.join(', ') }}</span>
          </div>
          <div v-if="summary.trigger" class="cc-sp-row">
            <span class="cc-sp-k">{{ summary.hideSendWindow ? 'Trigger' : 'Audience' }}</span><span class="cc-sp-v">{{ summary.trigger }}</span>
          </div>
          <div v-if="!summary.country && !summary.customer && !summary.journey && !(summary.channels && summary.channels.length)" class="cc-sp-empty">Not configured yet</div>
        </div>

        <div class="cc-sp-section">
          <div class="cc-sp-lbl"><span :class="['cc-sn', stepStatus(3)]">3</span> Content</div>
          <div v-if="summary.contentReady" class="cc-sp-row">
            <span class="cc-sp-k">Status</span><span class="cc-sp-v" style="color:#2E7D32">✓ Generated</span>
          </div>
          <div v-else class="cc-sp-empty">Not configured yet</div>
        </div>

        <div v-if="!summary.hideSendWindow" class="cc-sp-section">
          <div class="cc-sp-lbl"><span :class="['cc-sn', stepStatus(4)]">4</span> Send Window</div>
          <div v-if="summary.sendWindow" class="cc-sp-row">
            <span class="cc-sp-k">Window</span><span class="cc-sp-v">{{ summary.sendWindow }}</span>
          </div>
          <div v-else class="cc-sp-empty">Not configured yet</div>
        </div>

        <div class="cc-sp-section">
          <div class="cc-sp-lbl"><span :class="['cc-sn', stepStatus(guardrailStepNumber)]">{{ guardrailStepNumber }}</span> Guardrail Checks</div>
          <div class="cc-sp-check" v-for="check in guardrailChecks" :key="check">
            <span>{{ summary.guardrailsPassed ? '✓' : '○' }}</span><span :class="['cc-sp-check-text', { pass: summary.guardrailsPassed }]">{{ check }}</span>
          </div>
        </div>

        <div class="cc-sp-footer">
          <button class="cc-sp-schedule-btn" :disabled="!summary.guardrailsPassed">{{ summary.finishLabel || 'Schedule Campaign' }}</button>
          <div class="cc-sp-footer-hint">Complete all steps to {{ summary.hideSendWindow ? 'activate' : 'schedule' }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CampaignSummarySidebar',
  props: {
    currentStep: {
      type: Number,
      default: 1
    },
    summary: {
      type: Object,
      // journey/trigger/contentReady/guardrailsPassed/hideSendWindow/finishLabel are
      // populated by trigger-based flows (always-on, in-session), which have no audience
      // step and no send window. channels-only usage (batch) keeps the original shape.
      default: () => ({ type: null, channels: [] })
    }
  },
  data () {
    return {
      collapsed: false,
      sectionLabels: ['Campaign Type', 'Audience', 'Content', 'Send Window', 'Guardrail Checks']
    }
  },
  computed: {
    audienceSectionLabel () {
      return this.summary.hideSendWindow ? 'Trigger' : 'Audience'
    },
    guardrailStepNumber () {
      return this.summary.hideSendWindow ? 4 : 5
    },
    guardrailChecks () {
      const base = ['Frequency cap', '48h cooldown', 'Clinical safety', 'STOP excluded']
      // PRD §3.5's 8-item canonical checklist — the two flows differ on which of the
      // remaining four apply (see nudgeCampaignChat.js's reviewBlock for the full,
      // per-campaign N/A logic; this is the sidebar's compact preview).
      if (this.summary.hideSendWindow) return [...base, 'Template (if long-window)', 'Blackout window', 'Active conv. exclusion']
      return [...base, 'Template approved', 'Send window set', 'Blackout window', 'Active conv. exclusion']
    }
  },
  methods: {
    stepStatus (stepNumber) {
      if (stepNumber < this.currentStep) return 'done'
      if (stepNumber === this.currentStep) return 'active'
      return 'pending'
    }
  }
}
</script>
<style scoped src="./CampaignSummarySidebar.css"></style>



// WEBPACK FOOTER //
// CampaignSummarySidebar.vue?48fe4c2e